import { ArrowDownRight, ArrowRight, ArrowUpRight, Info, Minus } from 'lucide-react';
import { useState } from 'react';
import type { InvestmentConnection } from '@fd/schemas';
import { today, useEntities, useLoans, useNetWorthHistory, type DashboardData, type NetWorthRange } from '../../app/data.ts';
import { useT } from '../../app/i18n.tsx';
import { hrefFor } from '../../app/router.tsx';
import { HorizonChart } from '../../ui/HorizonChart.tsx';
import { Money, Segmented } from '../../ui/primitives.tsx';
import { useInTile } from '../../ui/tile.ts';
import { usePeriod } from '../../shell/Shell.tsx';
import { useBankConnections } from '../Banks.tsx';

// Financial snapshot, the top of the overview (the owner's brief, 29.09.2026): net worth leads with
// how it moved and its curve, and beside it three figures that are never mixed up with it —
// money available now, everything owned, everything owed.

const RANGES: readonly NetWorthRange[] = ['1M', '3M', '6M', 'YTD', '1Y', 'ALL'];
const CASH_TYPES = new Set(['CURRENT', 'CASH', 'SAVINGS']);
const DEBT_TYPES = new Set(['LOAN', 'MORTGAGE']);

/** How fresh the figures are: the last and next read of the banks and the investment platform. */
function Freshness() {
  const t = useT();
  const banks = useBankConnections();
  const investments = (useEntities<InvestmentConnection>('investmentConnection').data ?? []).filter((c) => c.deletedAt === null && c.status !== 'DISCONNECTED');
  const latest = (values: (string | null)[]) => values.filter((v): v is string => Boolean(v)).sort().at(-1) ?? null;
  const bankLast = latest(banks.map((c) => c.lastSuccessAt));
  const bankNext = banks
    .map((c) => c.nextRefreshAt)
    .filter((v): v is string => Boolean(v) && Date.parse(v!) > Date.now())
    .sort()[0];
  const investLast = latest(investments.map((c) => c.lastSyncedAt));
  const parts = [
    bankLast ? t.t(bankNext ? 'dashboard.freshBankNext' : 'dashboard.freshBank', { last: t.when(bankLast), next: bankNext ? t.when(bankNext) : '' }) : null,
    investLast ? t.t('dashboard.freshInvest', { time: t.when(investLast) }) : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return <p className="snapshot__fresh">{parts.join(' · ')}</p>;
}

/** One of the three figures beside net worth, with what it means one tap away. */
function Figure({ label, hint, children, note }: { label: string; hint: string; children: React.ReactNode; note?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="snapshot__figure">
      <dt>
        {label}
        <button type="button" className="snapshot__hint-toggle" aria-expanded={open} aria-label={`${label}: ${hint}`} title={hint} onClick={() => setOpen(!open)}>
          <Info aria-hidden="true" />
        </button>
      </dt>
      <dd>{children}</dd>
      {note && <dd className="snapshot__note">{note}</dd>}
      {open && <dd className="snapshot__hint">{hint}</dd>}
    </div>
  );
}

export function Snapshot({ data }: { data: DashboardData }) {
  const t = useT();
  const inTile = useInTile();
  const period = usePeriod();
  const [range, setRange] = useState<NetWorthRange>('6M');
  const history = useNetWorthHistory(range, null, data.period);
  const loans = useLoans();
  const currency = data.household.baseCurrency;
  const change = history?.periodChange ?? null;
  const before = change !== null ? data.netWorth.netMinor - change : null;
  const percent = change !== null && before !== null && before !== 0n ? Number((change * 10_000n) / (before < 0n ? -before : before)) / 100 : null;
  const Icon = change === null || change === 0n ? Minus : change > 0n ? ArrowUpRight : ArrowDownRight;
  const now = today();
  const running = data.period.start <= now && now <= data.period.end;
  const changeLabel = running
    ? t.t(period.kind === 'WEEK' ? 'dashboard.changeWeek' : period.kind === 'YEAR' ? 'dashboard.changeYear' : 'dashboard.changeMonth')
    : period.kind === 'YEAR'
      ? data.period.start.slice(0, 4)
      : t.date(data.period.start, period.kind === 'MONTH' ? 'monthYear' : 'dayMonth');

  const byId = new Map(data.accounts.map((a) => [a.id, a]));
  const available = data.netWorth.accounts.reduce((sum, v) => {
    const account = byId.get(v.accountId);
    return account && CASH_TYPES.has(account.type) && !account.archivedAt ? sum + v.baseMinor : sum;
  }, 0n);
  // Loans kept outside net worth still are debt: shown with the rest, and said so.
  const outside = loans
    .filter((l) => DEBT_TYPES.has(l.account.type) && !l.account.includeInNetWorth && !l.account.archivedAt && l.account.currency === currency)
    .reduce((sum, l) => sum + (l.status.remainingPayableMinor ?? l.status.outstandingMinor), 0n);
  const debt = data.netWorth.liabilitiesMinor + outside;

  return (
    <section className="card snapshot">
      <div className="snapshot__main">
        <div className="snapshot__head">
          <div className="stack" style={{ gap: 4, minWidth: 0 }}>
            <span className="snapshot__label">{t.t('netWorth.title')}</span>
            <span className="snapshot__value">
              <Money minor={data.netWorth.netMinor} currency={currency} animate />
            </span>
            {change !== null && (
              <span className={`snapshot__change ${change > 0n ? 'snapshot__change--up' : change < 0n ? 'snapshot__change--down' : ''}`}>
                <Icon aria-hidden="true" />
                <Money minor={change} currency={currency} options={{ signDisplay: 'exceptZero' }} />
                {percent !== null && (
                  <span className="snapshot__percent">
                    {new Intl.NumberFormat(t.locale, { maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(percent)}%
                  </span>
                )}
                <span className="snapshot__change-label">{changeLabel}</span>
              </span>
            )}
          </div>
          <a className="card__link" href={hrefFor({ page: 'netWorth' })}>
            {t.t('dashboard.viewAll')} <ArrowRight aria-hidden="true" />
          </a>
        </div>
        {data.netWorth.missingRates.length > 0 && (
          <p className="snapshot__warning" role="status">
            {t.plural('netWorth.missingRate', data.netWorth.missingRates.length)}
          </p>
        )}
        <div className="snapshot__chart">
          <Segmented<NetWorthRange>
            label={t.t('dashboard.historyRange')}
            value={range}
            onChange={setRange}
            options={RANGES.map((r) => ({ value: r, label: t.dynamic(`netWorth.rangeShort.${r}`) }))}
          />
          {/* On a dashboard card of a set size the curve takes the height left (the card follows its cells). */}
          <HorizonChart points={(history?.points ?? []).map((p) => ({ date: p.date, valueMinor: p.netMinor }))} currency={currency} height={inTile ? 'fill' : 150} ticks={4} label={t.t('dashboard.heroChart')} />
        </div>
        <Freshness />
      </div>
      <dl className="snapshot__figures">
        <Figure label={t.t('overview.availableCash')} hint={t.t('overview.availableCashHint')}>
          <Money minor={available} currency={currency} animate />
        </Figure>
        <Figure label={t.t('overview.assets')} hint={t.t('overview.assetsHint')}>
          <Money minor={data.netWorth.assetsMinor} currency={currency} animate />
        </Figure>
        <Figure
          label={t.t('overview.debt')}
          hint={t.t('overview.debtHint')}
          note={outside > 0n ? <span className="amount-text">{t.t('overview.debtOutside', { amount: t.money({ minor: outside, currency }) })}</span> : undefined}
        >
          <Money minor={debt} currency={currency} animate />
        </Figure>
      </dl>
    </section>
  );
}
